import * as XLSX from "xlsx";
import { getFinplanPlanType, mapFinplanPlanType } from "./finplanPlanTypes";

export const FINPLAN_TEMPLATE_HEADERS = [
  "Recipient",
  "Payment Created",
  "Selling Adviser",
  "Client Name",
  "Policy Number",
  "Policy Provider",
  "Business Type",
  "Commission Type",
  "Payment Amount",
  "Gross Received",
  "Split",
  "Introducers",
];

export const FINPLAN_TABLE_HEADERS = [
  "Index",
  ...FINPLAN_TEMPLATE_HEADERS,
  "RecipientEmail",
  "SellingAdviserEmail",
  "Source",
];

const uniqueHeaders = (headerRow) => {
  const counts = new Map();
  return headerRow.map((value, index) => {
    const base = String(value ?? "").trim() || `Column ${index + 1}`;
    const count = (counts.get(base) || 0) + 1;
    counts.set(base, count);
    return count === 1 ? base : `${base} (${count})`;
  });
};

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const createUtcDate = (year, month, day) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
    ? date
    : null;
};

const repairLegacyUsDate = (date, statementMonth) => {
  const selected = String(statementMonth || "").match(/^(\d{4})-(\d{2})$/);
  if (!selected || !date) return date;
  const selectedYear = Number(selected[1]);
  const selectedMonth = Number(selected[2]);
  if (
    date.getUTCFullYear() === selectedYear
    && date.getUTCMonth() + 1 === selectedMonth
  ) return date;

  // Older imports treated Excel's m/d/yy display as d/m/yy. Reverse that
  // overflow when the resulting day identifies the selected statement month.
  if (date.getUTCDate() !== selectedMonth) return date;
  const originalDay = ((date.getUTCFullYear() - selectedYear) * 12)
    + date.getUTCMonth() + 1;
  return createUtcDate(selectedYear, selectedMonth, originalDay) || date;
};

export const formatStatementIdentifier = (value) => {
  if (typeof value === "number" && Number.isSafeInteger(value)) {
    return value.toLocaleString("en-US", {
      useGrouping: false,
      maximumFractionDigits: 0,
    });
  }
  return String(value ?? "").trim();
};

export const normaliseFinplanSplit = (value) => {
  const source = String(value ?? "").trim();
  if (!source) return "";
  const parsed = Number(source.replace(/[% ,]/g, ""));
  if (!Number.isFinite(parsed)) return source;
  const percentage = !source.includes("%") && Math.abs(parsed) <= 1
    ? parsed * 100
    : parsed;
  return String(Number(percentage.toFixed(4)));
};

export const formatFinplanSplit = (value) => {
  const percentage = normaliseFinplanSplit(value);
  const parsed = Number(percentage);
  return Number.isFinite(parsed)
    ? `${parsed.toLocaleString("en-GB", { maximumFractionDigits: 4 })}%`
    : percentage || "-";
};

export const formatFinplanDate = (value, statementMonth = "") => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${String(value.getUTCDate()).padStart(2, "0")} ${monthNames[value.getUTCMonth()]} ${String(value.getUTCFullYear()).slice(-2)}`;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return String(value);
    const date = createUtcDate(parsed.y, parsed.m, parsed.d);
    if (!date) return String(value);
    return `${String(date.getUTCDate()).padStart(2, "0")} ${monthNames[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(-2)}`;
  }
  const text = String(value ?? "").trim();
  if (!text) return "";
  const dayFirst = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  const shortMonth = text.match(/^(\d{1,2})\s+([a-z]{3})\s+(\d{2}|\d{4})$/i);
  let date;
  if (dayFirst) {
    const year = Number(dayFirst[3]) + (dayFirst[3].length === 2 ? 2000 : 0);
    const first = Number(dayFirst[1]);
    const second = Number(dayFirst[2]);
    const selectedMonth = Number(String(statementMonth).slice(5, 7));
    const isMonthFirst = second > 12
      || (first <= 12 && first === selectedMonth && second !== selectedMonth);
    date = isMonthFirst
      ? createUtcDate(year, first, second)
      : createUtcDate(year, second, first);
  } else if (iso) {
    date = new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  } else if (shortMonth) {
    const month = [
      "jan", "feb", "mar", "apr", "may", "jun",
      "jul", "aug", "sep", "oct", "nov", "dec",
    ].indexOf(shortMonth[2].toLowerCase());
    if (month < 0) return text;
    const year = Number(shortMonth[3]) + (shortMonth[3].length === 2 ? 2000 : 0);
    date = new Date(Date.UTC(year, month, Number(shortMonth[1])));
  } else {
    date = new Date(text);
  }
  if (!date || Number.isNaN(date.getTime())) return text;
  date = repairLegacyUsDate(date, statementMonth);
  return `${String(date.getUTCDate()).padStart(2, "0")} ${monthNames[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(-2)}`;
};

export const matrixToFinplanStatement = (matrix = [], statementMonth = "") => {
  const populatedRows = matrix.filter(
    (row) => Array.isArray(row) && row.some((cell) => String(cell ?? "").trim()),
  );
  if (populatedRows.length < 2) {
    throw new Error("The selected file does not contain any statement entries.");
  }
  const headers = uniqueHeaders(populatedRows[0]);
  const rows = populatedRows.slice(1).map((row) =>
    headers.reduce((entry, header, index) => {
      const sourceValue = row[index] ?? "";
      const value = String(sourceValue).trim();
      const normalisedHeader = header.toLowerCase();
      entry[header] = normalisedHeader === "payment created"
        ? formatFinplanDate(sourceValue, statementMonth)
        : ["policy number", "policy account number"].includes(normalisedHeader)
          ? formatStatementIdentifier(sourceValue)
          : normalisedHeader === "split"
            ? normaliseFinplanSplit(sourceValue)
            : value;
      return entry;
    }, {}),
  );
  return { headers, rows };
};

export const normaliseFinplanRows = (rows = [], statementMonth = "") =>
  rows.map((row, index) => ({
    Index: String(index + 1),
    Recipient: String(row.Recipient ?? "").trim(),
    RecipientEmail: String(
      row.RecipientEmail ?? row["Recipient Email"] ?? "",
    ).trim(),
    SourceAdviserName: String(row.Recipient ?? "").trim(),
    MappedAdviserName: String(row.Recipient ?? "").trim(),
    PaymentCreated: formatFinplanDate(
      row.PaymentCreated ?? row["Payment Created"],
      statementMonth,
    ),
    PolicyAdviser: String(
      row.PolicyAdviser ?? row["Selling Adviser"] ?? "",
    ).trim(),
    SellingAdviserEmail: String(
      row.SellingAdviserEmail ?? row["Selling Adviser Email"] ?? "",
    ).trim(),
    ClientName: String(row.ClientName ?? row["Client Name"] ?? "").trim(),
    PolicyAccountNumber: String(
      formatStatementIdentifier(
        row.PolicyAccountNumber ?? row["Policy Number"] ?? "",
      ),
    ).trim(),
    PolicyProvider: String(
      row.PolicyProvider ?? row["Policy Provider"] ?? "",
    ).trim(),
    BusinessType: mapFinplanPlanType(getFinplanPlanType(row)),
    FinplanPlanType: getFinplanPlanType(row),
    CommissionType: String(
      row.CommissionType ?? row["Commission Type"] ?? "",
    ).trim(),
    PaymentAmount: String(
      row.PaymentAmount ?? row["Payment Amount"] ?? "",
    ).trim(),
    GrossReceived: String(
      row.GrossReceived ?? row["Gross Received"] ?? "",
    ).trim(),
    Split: normaliseFinplanSplit(row.Split),
    Introducers: String(row.Introducers ?? "").trim(),
    Source: "Finplan",
    __source: "finplan",
  }));

export const parseFinplanStatementFile = async (file, statementMonth = "") => {
  if (!file) throw new Error("Select a statement to upload.");
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (!["csv", "xlsx", "xls"].includes(extension)) {
    throw new Error("Upload a CSV or Excel file.");
  }
  const workbook = XLSX.read(await file.arrayBuffer(), {
    type: "array",
    cellDates: false,
  });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) throw new Error("The selected workbook does not contain a sheet.");
  return matrixToFinplanStatement(
    XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], {
      header: 1,
      defval: "",
      raw: true,
    }),
    statementMonth,
  );
};
